import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Button, Typography } from '@mui/material';
import AddRemoveIcon from '@sharedCR/common/AddRemoveIcon';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COLOR_PALETTE } from 'constants/ui';

import {
  selectApplicationEscrowData,
  selectTaxesInsuranceEscrowedLoanFeesView,
} from 'store/loanApplications/selectors';
import { selectExistingImpoundTypesOptions } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../../../LoanFees.testIds';
import { addButtonStyles } from '../../../../AccordionDetailsView/FeeTable/FeeTable.styles';

import ImpoundItem from './ImpoundItem';

const ImpoundList = ({
  formData,
  monthsCushion = null,
  isLoading,
  setIsLoading,
  meta,
  onAddImpound,
  onDeleteImpound,
}) => {
  const allImpoundTypesOptions = useSelector(selectExistingImpoundTypesOptions);
  const { isEscrowNotAllowed, floodInsuranceImpoundId } = useSelector(selectApplicationEscrowData);
  const taxesInsuranceEscrowed = useSelector(selectTaxesInsuranceEscrowedLoanFeesView);

  const filteredImpoundTypesOptions = useMemo(() => {
    if (isEscrowNotAllowed) {
      return allImpoundTypesOptions.filter(({ value }) => value !== floodInsuranceImpoundId);
    }

    return allImpoundTypesOptions;
  }, [isEscrowNotAllowed, floodInsuranceImpoundId, allImpoundTypesOptions]);

  const usedImpoundTypeIds = useMemo(() => _.map(formData, 'impoundTypeId.value'), [formData]);

  return (
    <div>
      {formData.map((formImpound, impoundInd) => {
        const impoundTypesOptions = filteredImpoundTypesOptions.filter(
          ({ value }) =>
            formImpound.impoundTypeId?.value === value || !usedImpoundTypeIds.includes(value),
        );

        return (
          <ImpoundItem
            impound={formImpound}
            impoundTypesOptions={impoundTypesOptions}
            index={impoundInd}
            isLoading={isLoading}
            key={formImpound.applicationImpoundId}
            meta={meta}
            monthsCushion={monthsCushion}
            setIsLoading={setIsLoading}
            onDeleteImpound={onDeleteImpound}
          />
        );
      })}

      <div className="flex justify-start">
        <Button
          data-testid={LoanFeesTestIds.IMPOUND_LIST_ADD_BUTTON}
          disabled={isLoading || !taxesInsuranceEscrowed}
          sx={addButtonStyles}
          variant="text"
          onClick={onAddImpound}
        >
          <AddRemoveIcon color={COLOR_PALETTE.BRAND[6]} />
          <Typography variant="regular14">Add Impound</Typography>
        </Button>
      </div>
    </div>
  );
};

ImpoundList.propTypes = {
  ...WithLoadingPropTypes,
  formData: PropTypes.array.isRequired,
  meta: PropTypes.object.isRequired,
  onAddImpound: PropTypes.func.isRequired,
  onDeleteImpound: PropTypes.func.isRequired,
  monthsCushion: PropTypes.number,
};

export default ImpoundList;
